import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SignUp.module.css';

const scienceFields = [
  'Biology',
  'Chemistry',
  'Physics',
  'Environmental Science',
  'Computer Science',
  'Neuroscience',
  'Astronomy',
  'Other',
];

const INITIAL_FORM = {
  fullName: '',
  age: '',
  country: '',
  city: '',
  field: '',
  bio: '',
  email: '',
  password: '',
};

const SignUp = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(e => ({ ...e, [name]: '' }));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'Tell us your name';
    if (!form.age || Number(form.age) < 8 || Number(form.age) > 99) errs.age = 'Enter a valid age';
    if (!form.country.trim()) errs.country = 'Where are you from?';
    if (!form.field) errs.field = 'Pick your field';
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!form.bio.trim()) errs.bio = 'Share a sentence about yourself';
    if (!form.email.includes('@')) errs.email = 'Enter a valid email';
    if (form.password.length < 6) errs.password = 'At least 6 characters';
    return errs;
  };

  const handleNextStep = () => {
    const errs = validateStep1();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validateStep2();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    // Store in sessionStorage for the document page
    sessionStorage.setItem('labora_user', JSON.stringify(form));
    navigate('/document');
  };

  return (
    <main className={styles.main}>
      {/* Background glow */}
      <div className={styles.glow} aria-hidden="true" />

      <div className={styles.container}>
        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.logoRow}>
            <svg className={styles.logoIcon} viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <path d="M11 4h10M13 4v9.5L6.5 22a5 5 0 004.3 7.5h10.4A5 5 0 0025.5 22L19 13.5V4"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12.5" cy="22.5" r="1.5" fill="currentColor" opacity="0.7"/>
              <circle cx="18" cy="24.5" r="1" fill="currentColor" opacity="0.5"/>
              <circle cx="15" cy="20.5" r="1" fill="currentColor" opacity="0.6"/>
            </svg>
            <span className={styles.logoText}>Labora</span>
          </div>
          <p className={styles.tagline}>Science is better when it's shared.</p>
        </div>

        {/* ── Progress indicator ── */}
        <div className={styles.progress}>
          <div className={`${styles.progressStep} ${step >= 1 ? styles.progressActive : ''}`}>
            <span className={styles.progressDot}>1</span>
            <span className={styles.progressLabel}>About you</span>
          </div>
          <div className={styles.progressLine} />
          <div className={`${styles.progressStep} ${step >= 2 ? styles.progressActive : ''}`}>
            <span className={styles.progressDot}>2</span>
            <span className={styles.progressLabel}>Your account</span>
          </div>
        </div>

        {/* ── Form card ── */}
        <div className={styles.card}>
          {step === 1 ? (
            <>
              <div className={styles.cardHeader}>
                <h1 className={styles.cardTitle}>Welcome to Labora</h1>
                <p className={styles.cardSub}>
                  Let's get to know you. Every great scientist started somewhere — you're starting here.
                </p>
              </div>

              <div className={styles.fields}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="fullName">Full name</label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    className={`${styles.input} ${errors.fullName ? styles.inputError : ''}`}
                    placeholder="e.g. Aya Kobrosli"
                    value={form.fullName}
                    onChange={handleChange}
                    autoFocus
                  />
                  {errors.fullName && <span className={styles.error}>{errors.fullName}</span>}
                </div>

                <div className={styles.row}>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="age">Age</label>
                    <input
                      id="age"
                      name="age"
                      type="number"
                      min="8"
                      max="99"
                      className={`${styles.input} ${errors.age ? styles.inputError : ''}`}
                      placeholder="e.g. 17"
                      value={form.age}
                      onChange={handleChange}
                    />
                    {errors.age && <span className={styles.error}>{errors.age}</span>}
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="country">Country</label>
                    <input
                      id="country"
                      name="country"
                      type="text"
                      className={`${styles.input} ${errors.country ? styles.inputError : ''}`}
                      placeholder="e.g. Lebanon"
                      value={form.country}
                      onChange={handleChange}
                    />
                    {errors.country && <span className={styles.error}>{errors.country}</span>}
                  </div>
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="city">City <span className={styles.optional}>(optional)</span></label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    className={styles.input}
                    placeholder="e.g. Beirut"
                    value={form.city}
                    onChange={handleChange}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="field">Field of science</label>
                  <div className={styles.selectWrap}>
                    <select
                      id="field"
                      name="field"
                      className={`${styles.select} ${errors.field ? styles.inputError : ''}`}
                      value={form.field}
                      onChange={handleChange}
                    >
                      <option value="">Select your field…</option>
                      {scienceFields.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    <svg className={styles.selectArrow} viewBox="0 0 16 16" fill="none">
                      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5"
                            strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {errors.field && <span className={styles.error}>{errors.field}</span>}
                </div>
              </div>

              <button className={styles.nextBtn} onClick={handleNextStep}>
                Next step
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8"
                        strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.cardHeader}>
                <h1 className={styles.cardTitle}>
                  Great to meet you, {form.fullName.split(' ')[0]}! 👋
                </h1>
                <p className={styles.cardSub}>
                  One more step. Tell us a little about your science, then create your account.
                </p>
              </div>

              <div className={styles.fields}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="bio">
                    About you in one sentence
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    rows={3}
                    className={`${styles.textarea} ${errors.bio ? styles.inputError : ''}`}
                    placeholder='e.g. "I study how soil bacteria respond to climate stress and I want to share every step of my research."'
                    value={form.bio}
                    onChange={handleChange}
                  />
                  {errors.bio && <span className={styles.error}>{errors.bio}</span>}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="email">Email address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={handleChange}
                  />
                  {errors.email && <span className={styles.error}>{errors.email}</span>}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="password">Create a password</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                    placeholder="At least 6 characters"
                    value={form.password}
                    onChange={handleChange}
                  />
                  {errors.password && <span className={styles.error}>{errors.password}</span>}
                </div>
              </div>

              <div className={styles.formActions}>
                <button type="button" className={styles.backBtn} onClick={() => setStep(1)}>
                  ← Back
                </button>
                <button type="submit" className={styles.submitBtn}>
                  Create my account &amp; start documenting
                  <svg viewBox="0 0 20 20" fill="none">
                    <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8"
                          strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </form>
          )}
        </div>

        <p className={styles.signInNote}>
          Already have an account? <span className={styles.signInLink}>Sign in</span>
        </p>
      </div>
    </main>
  );
};

export default SignUp;
